import type { JSX } from "react";
import { cn } from "./cn";

export interface MonthEvent {
  readonly title: string;
  readonly color: string;
  readonly dayIndex: number;
}

export interface MonthCalendarProps {
  /** Events plotted on the month grid */
  readonly events: readonly MonthEvent[];
  /** Number of days to render (default: 30) */
  readonly dayCount?: number;
  /** Extra className */
  readonly className?: string;
}

const colorMap: Readonly<Record<string, string>> = {
  blue: "bg-blue text-white",
  green: "bg-green text-on-bright",
  orange: "bg-amber text-on-bright",
  red: "bg-red text-white",
};

const fallback = "bg-surface-hover text-content";

/**
 * A month-view calendar grid with optional event indicators.
 */
export function MonthCalendar({ events, dayCount = 30, className }: MonthCalendarProps): JSX.Element {
  const eventByDay = new Map(events.map((e) => [e.dayIndex, e]));

  return (
    <div className={cn("grid grid-cols-7 gap-px bg-edge rounded-lg overflow-hidden", className)}>
      {Array.from({ length: dayCount }, (_, index) => {
        const event = eventByDay.get(index);
        return (
          <div key={index} className="bg-surface min-h-[60px] p-1.5 flex flex-col gap-1">
            <span className="text-[10px] text-content-tertiary">{index + 1}</span>
            {event ? (
              <div
                className={cn(
                  "rounded px-1 py-0.5 text-[9px] truncate",
                  colorMap[event.color] ?? fallback,
                )}
              >
                {event.title}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
